"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Hash, Send, Users, Circle } from "lucide-react";

interface Msg { id: string; channelId: string; author: string; role: string; text: string; ts: number; }
interface Channel { id: string; name: string; icon: string; }

const SEED_CHANNELS: Channel[] = [
  { id: "general", name: "general", icon: "💬" },
  { id: "sales", name: "sales-team", icon: "📈" },
  { id: "warehouse", name: "warehouse-ops", icon: "📦" },
  { id: "finance", name: "finance", icon: "💰" },
  { id: "logistics", name: "logistics", icon: "🚚" },
];

const SEED_MSGS: Msg[] = [
  { id: "m1", channelId: "general", author: "Arif Rahman", role: "CEO", text: "Good morning team. Daily standup at 9:30 AM.", ts: Date.now() - 3600000 },
  { id: "m2", channelId: "general", author: "Sadia Islam", role: "Head of Sales", text: "On my way. SO-2026-8824 dispatched yesterday.", ts: Date.now() - 3300000 },
  { id: "m3", channelId: "general", author: "Md. Hasan", role: "Warehouse Sup.", text: "GRN for PO-1024 completed. Stock updated.", ts: Date.now() - 3000000 },
  { id: "m4", channelId: "sales", author: "Sadia Islam", role: "Head of Sales", text: "Rahim Distributors wants 200 more units of Sunflower Oil.", ts: Date.now() - 1800000 },
  { id: "m5", channelId: "warehouse", author: "Md. Hasan", role: "Warehouse Sup.", text: "Reorder alert for Tea Grade A 500g — only 1800 left.", ts: Date.now() - 1500000 },
  { id: "m6", channelId: "finance", author: "Rumana Begum", role: "Finance Mgr", text: "RCT-2026-4482 reconciled. AR updated.", ts: Date.now() - 900000 },
  { id: "m7", channelId: "logistics", author: "Rakib Hossain", role: "Driver", text: "SHP-2026-1024 delivered. POD captured.", ts: Date.now() - 600000 },
];

const ONLINE_USERS = [
  { name: "Arif Rahman", role: "CEO", dept: "Executive", color: "bg-amber-500" },
  { name: "Sadia Islam", role: "Head of Sales", dept: "Sales", color: "bg-rose-500" },
  { name: "Imran Khan", role: "Sr. Sales Officer", dept: "Sales", color: "bg-rose-400" },
  { name: "Rumana Begum", role: "Finance Manager", dept: "Finance", color: "bg-violet-500" },
  { name: "Md. Hasan", role: "Warehouse Sup.", dept: "Warehouse", color: "bg-emerald-500" },
  { name: "Rakib Hossain", role: "Driver", dept: "Logistics", color: "bg-cyan-500" },
];

export function InternalChatPage() {
  const [activeChannel, setActiveChannel] = React.useState("general");
  const [messages, setMessages] = React.useState<Msg[]>(SEED_MSGS);
  const [input, setInput] = React.useState("");
  const me = "Sadia Islam";
  const myRole = "Head of Sales";
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, activeChannel]);

  const send = () => {
    if (!input.trim()) return;
    setMessages(m => [...m, { id: `m${Date.now()}`, channelId: activeChannel, author: me, role: myRole, text: input.trim(), ts: Date.now() }]);
    setInput("");
    // Simulate auto-reply
    setTimeout(() => {
      const repliers = ONLINE_USERS.filter(u => u.name !== me);
      const r = repliers[Math.floor(Math.random() * repliers.length)];
      const replies = ["Got it 👍", "On it.", "Will check and revert.", "Thanks for the update.", "Noted."];
      setMessages(m => [...m, { id: `m${Date.now() + 1}`, channelId: activeChannel, author: r.name, role: r.role, text: replies[Math.floor(Math.random() * replies.length)], ts: Date.now() }]);
    }, 1200 + Math.random() * 1500);
  };

  const channelMsgs = messages.filter(m => m.channelId === activeChannel);
  const activeChannelObj = SEED_CHANNELS.find(c => c.id === activeChannel);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">Team Chat</h1>
        <p className="text-muted-foreground mt-1 text-sm">Real-time internal messaging across departments.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {/* Channels list */}
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="flex items-center gap-2 text-sm"><Hash className="size-4" /> Channels</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-1">
            {SEED_CHANNELS.map(c => (
              <button key={c.id} onClick={() => setActiveChannel(c.id)}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${activeChannel === c.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                <span>{c.icon}</span> <span className="font-medium">{c.name}</span>
              </button>
            ))}
            <div className="mt-4 border-t pt-3">
              <p className="text-muted-foreground mb-2 flex items-center gap-1 px-3 text-xs font-medium uppercase"><Users className="size-3" /> Online ({ONLINE_USERS.length})</p>
              <div className="omega-sidebar-scroll max-h-60 overflow-y-auto">
                {ONLINE_USERS.map(u => (
                  <div key={u.name} className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm hover:bg-muted/50">
                    <div className="relative">
                      <Avatar className="size-7"><AvatarFallback className={`${u.color} text-white text-[10px]`}>{u.name.split(" ").map(x => x[0]).join("").slice(0, 2)}</AvatarFallback></Avatar>
                      <Circle className="absolute -bottom-0.5 -right-0.5 size-2.5 fill-emerald-500 text-emerald-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium">{u.name}</p>
                      <p className="text-muted-foreground truncate text-[10px]">{u.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Chat */}
        <Card className="lg:col-span-3">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2 text-base">
              <span>{activeChannelObj?.icon}</span>
              <span>#{activeChannelObj?.name}</span>
              <Badge variant="secondary" className="ml-2 text-[10px]">{channelMsgs.length} messages</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex h-96 flex-col p-0">
            <div ref={scrollRef} className="omega-sidebar-scroll flex-1 overflow-y-auto p-4">
              {channelMsgs.length === 0 ? (
                <p className="text-muted-foreground py-12 text-center text-sm">No messages yet. Start the conversation!</p>
              ) : channelMsgs.map(m => {
                const isMe = m.author === me;
                return (
                  <div key={m.id} className={`mb-3 flex gap-2 ${isMe ? "flex-row-reverse" : ""}`}>
                    <Avatar className="size-8 shrink-0">
                      <AvatarFallback className={isMe ? "bg-rose-500 text-white text-[10px]" : "bg-muted text-[10px]"}>
                        {m.author.split(" ").map(x => x[0]).join("").slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div className={`max-w-[75%] ${isMe ? "items-end text-right" : ""} flex flex-col`}>
                      <div className="mb-0.5 flex items-center gap-1.5 text-xs">
                        <span className="font-medium">{m.author}</span>
                        <span className="text-muted-foreground">{m.role}</span>
                        <span className="text-muted-foreground">· {new Date(m.ts).toLocaleTimeString("en-BD", { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                      <div className={`inline-block rounded-lg px-3 py-2 text-sm ${isMe ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                        {m.text}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 border-t p-3">
              <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") send(); }} placeholder={`Message #${activeChannelObj?.name}...`} />
              <Button onClick={send} disabled={!input.trim()}><Send className="size-4" /></Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
